// src/pages/DealerPage.tsx — Dealer Panel (real per-dealer data)
import React, { useState, useEffect, useCallback } from 'react';
import { getDealers, getBags } from '../services/api';
import type { Bag, Dealer } from '../types';

export default function DealerPage() {
  const [dealers,  setDealers]  = useState<Dealer[]>([]);
  const [dealerId, setDealerId] = useState('');
  const [bags,     setBags]     = useState<Bag[]>([]);
  const [stats,    setStats]    = useState({ total: 0, delivered: 0, fraud: 0 });
  const [loading,  setLoading]  = useState(true);

  // Load dealer list once
  useEffect(() => {
    getDealers()
      .then(r => {
        const list = r.data.data || [];
        setDealers(list);
        if (list.length) setDealerId(list[0].id);
        else setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Load the selected dealer's real stats + recent bags
  const load = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    try {
      const [allRes, delRes, fraudRes] = await Promise.all([
        getBags({ dealer_id: dealerId, limit: 50 }),
        getBags({ dealer_id: dealerId, status: 'DELIVERED', limit: 1 }),
        getBags({ dealer_id: dealerId, status: 'FRAUD',     limit: 1 }),
      ]);
      setBags(allRes.data.data || []);
      setStats({
        total:     allRes.data.total   || 0,
        delivered: delRes.data.total   || 0,
        fraud:     fraudRes.data.total || 0,
      });
    } catch {
      setBags([]);
      setStats({ total: 0, delivered: 0, fraud: 0 });
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => { load(); }, [load]);

  const dealer    = dealers.find(d => d.id === dealerId);
  const remaining = Math.max(stats.total - stats.delivered, 0);
  const pct       = stats.total ? Math.round((stats.delivered / stats.total) * 100) : 0;
  const initials  = dealer?.name?.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase() || '🏬';

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">Dealer Panel — Track Your Delivery Targets</div>
        {dealers.length > 0 && (
          <select
            value={dealerId}
            onChange={e => setDealerId(e.target.value)}
            style={{ padding:'7px 12px', border:'1.5px solid var(--bor)', borderRadius:9, fontSize:13, background:'var(--card)', outline:'none', maxWidth:260 }}
          >
            {dealers.map(d => <option key={d.id} value={d.id}>{d.name} ({d.dealerCode})</option>)}
          </select>
        )}
      </div>

      <div className="page-content fade-in">
        <div className="grid-2" style={{ alignItems:'start' }}>

          {/* Dealer info + real stats */}
          <div className="card">
            <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:20, padding:'14px', background:'var(--gpp)', borderRadius:10 }}>
              <div style={{ width:48, height:48, borderRadius:12, background:'var(--g2)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:700 }}>{initials}</div>
              <div>
                <div style={{ fontSize:16, fontWeight:700, color:'var(--ink)' }}>{dealer?.name || 'Select a dealer'}</div>
                <div style={{ fontSize:12, color:'var(--ink3)' }}>
                  {dealer ? `${dealer.dealerCode} | ${dealer.districtCode || ''} | ${dealer.state || ''}` : '—'}
                </div>
              </div>
            </div>

            {/* Ring + real stats */}
            <div style={{ display:'flex', alignItems:'center', gap:20, marginBottom:20 }}>
              <div style={{ textAlign:'center' }}>
                <div style={{ fontSize:36, fontWeight:800, color:'var(--g2)', lineHeight:1 }}>{pct}%</div>
                <div style={{ fontSize:11, color:'var(--ink3)' }}>Delivered</div>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ height:10, background:'var(--bg)', borderRadius:10, overflow:'hidden', border:'1px solid var(--bor)', marginBottom:8 }}>
                  <div style={{ height:'100%', width:`${pct}%`, background:'linear-gradient(90deg,var(--g3),var(--g4))', borderRadius:10, transition:'width .6s' }}></div>
                </div>
                <div className="grid-2" style={{ gap:8 }}>
                  {([
                    ['Total Assigned', stats.total],
                    ['Delivered',      stats.delivered],
                    ['Remaining',      remaining],
                    ['Fraud Flags',    stats.fraud],
                  ] as const).map(([l,v]) => (
                    <div key={l} style={{ padding:'8px 10px', background:'var(--bg)', borderRadius:8, border:'1px solid var(--bor)' }}>
                      <div style={{ fontSize:16, fontWeight:700, color: l==='Fraud Flags' && v>0 ? 'var(--red)' : 'var(--g2)' }}>{v.toLocaleString('en-IN')}</div>
                      <div style={{ fontSize:10, color:'var(--ink3)' }}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Real bag table */}
            <div className="tbl-scroll">
              <table className="tbl">
                <thead><tr><th>Bag ID</th><th>Status</th><th>Farmer</th><th>Date</th></tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={4} style={{ textAlign:'center', padding:24 }}><span className="spin">⟳</span></td></tr>
                  ) : bags.length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign:'center', padding:24, color:'var(--ink3)' }}>No bags for this dealer</td></tr>
                  ) : bags.slice(0,8).map(b => (
                    <tr key={b.bagId || b.id}>
                      <td className="mono" style={{ fontSize:11 }}>{b.bagId}</td>
                      <td><span className={`tag ${b.status==='DELIVERED'?'tag-green':b.status==='FRAUD'?'tag-red':'tag-amber'}`}>{b.status}</span></td>
                      <td style={{ fontSize:12 }}>{b.farmerName || '—'}</td>
                      <td style={{ fontSize:11, color:'var(--ink3)' }}>{b.deliveredAt ? new Date(b.deliveredAt).toLocaleDateString('en-IN') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {bags.length > 8 && (
              <div style={{ marginTop:10, fontSize:11, color:'var(--ink3)', textAlign:'center' }}>
                Showing 8 of {stats.total} bags
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="card">
            <div className="card-title">How to scan bags</div>
            {[
              { step:1, title:'Receive the bags',      desc:'After bags arrive from the warehouse, do a Warehouse Scan.' },
              { step:2, title:'Hand bag to farmer',    desc:'Before giving the bag to the farmer, do a Retailer Scan.' },
              { step:3, title:'Farmer scans the QR',   desc:'The farmer scans the bag QR from their phone — it auto-registers.' },
            ].map(s => (
              <div key={s.step} style={{ display:'flex', gap:12, marginBottom:16 }}>
                <div style={{ width:28, height:28, borderRadius:'50%', background:'var(--g2)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, flexShrink:0 }}>{s.step}</div>
                <div><div style={{ fontSize:13, fontWeight:600, color:'var(--ink)' }}>{s.title}</div><div style={{ fontSize:12, color:'var(--ink3)', marginTop:2 }}>{s.desc}</div></div>
              </div>
            ))}
            <div style={{ marginTop:8, padding:'10px 12px', background:'var(--gpp)', borderRadius:8, fontSize:11, color:'var(--g2)' }}>
              ✅ Every scan shows up live on the government dashboard.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
